/* Magazine — shared utilities (formatting, fetcher, types-as-jsdoc). */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/magazine`;

export async function listArticles(params = {}) {
  const { data } = await axios.get(`${API}/articles`, { params });
  return data;
}

export async function getArticle(slug) {
  const { data } = await axios.get(`${API}/articles/${slug}`);
  return data;
}

export async function listCategories() {
  const { data } = await axios.get(`${API}/categories`);
  return data;
}

export async function getCategory(slug) {
  const { data } = await axios.get(`${API}/categories/${slug}`);
  return data;
}

export async function listAuthors() {
  const { data } = await axios.get(`${API}/authors`);
  return data;
}

export async function getAuthor(slug) {
  const { data } = await axios.get(`${API}/authors/${slug}`);
  return data;
}

export function formatPublishedAt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * Tiny markdown-ish renderer for article bodies.
 * Handles: paragraphs (\n\n), ## headings, - bullets, 1. ordered lists,
 * --- horizontal rule, **bold**, *italic*.
 * Anything richer than this should later move to a proper markdown lib.
 */
const LEAD_RE = /^((?:Phase|Fase|Step|Paso|Day|D[íi]a|Week|Semana|Pillar|Pilar|Myth|Mito|Part|Parte|Tip|Rule|Regla|Secret|Secreto|Mistake|Error|Stage|Etapa|Level|Nivel)\s*#?\d+\s*[:.\-–—]\s*)(.*)$/is;

export function renderBody(body) {
  if (!body) return [];
  const blocks = body.split(/\n\n+/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block, idx) => {
    if (block === '---') {
      return { type: 'hr', key: idx };
    }
    if (block.startsWith('### ')) {
      return { type: 'h3', key: idx, text: block.slice(4) };
    }
    if (block.startsWith('## ')) {
      return { type: 'h2', key: idx, text: block.slice(3) };
    }
    if (/^- /m.test(block)) {
      const items = block.split(/\n/).filter((l) => l.startsWith('- ')).map((l) => l.slice(2));
      return { type: 'ul', key: idx, items };
    }
    if (/^\d+\.\s/m.test(block)) {
      const items = block.split(/\n/).filter((l) => /^\d+\.\s/.test(l)).map((l) => l.replace(/^\d+\.\s+/, ''));
      return { type: 'ol', key: idx, items };
    }
    // "Phase 01: Structured Data Hygiene. The first step…" → bold lead-in + body
    const m = block.match(LEAD_RE);
    if (m) {
      const rest = m[2];
      const dot = rest.search(/[.!?]\s/);
      if (dot > -1 && dot < 90) {
        return { type: 'p', key: idx, lead: (m[1] + rest.slice(0, dot + 1)).trim(), text: rest.slice(dot + 1).trim() };
      }
      if (rest.length <= 90) return { type: 'h3', key: idx, text: (m[1] + rest).trim() };
      return { type: 'p', key: idx, lead: m[1].trim(), text: rest };
    }
    // Standalone short line without terminal punctuation → subtitle
    const words = block.split(/\s+/);
    if (!block.includes('\n') && block.length <= 70 && words.length >= 2 && words.length <= 9 &&
        !/[.!?;:,]$/.test(block) && !/^https?:/i.test(block) && !block.startsWith('[') && !block.startsWith('!') && !block.startsWith('>')) {
      return { type: 'h3', key: idx, text: block.replace(/\*\*/g, '') };
    }
    return { type: 'p', key: idx, text: block };
  });
}

export function inlineFormat(text) {
  // Tokenize **bold** and *italic*. Bold takes precedence (matched first).
  const parts = [];
  const re = /\*\*([^*]+)\*\*|\*([^*\n]+)\*/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ t: text.slice(last, m.index) });
    if (m[1] !== undefined) {
      parts.push({ t: m[1], b: true });
    } else {
      parts.push({ t: m[2], i: true });
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ t: text.slice(last) });
  return parts;
}
